import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, DOMAIN_COLORS } from "@/lib/api";

const VALUE_BULLETS = [
  { k: "01", t: "Scientific", d: "9 domains · 45 constructs · 225 validated items grounded in psychometric theory." },
  { k: "02", t: "Personalised", d: "An elaborate report with scores, interpretation, inferences, and recommendations." },
  { k: "03", t: "Flexible", d: "Choose 3 to 9 specialization domains. Calibrate the assessment to your time." },
  { k: "04", t: "Actionable", d: "Construct-level insights pinpoint why a specialization may or may not fit you." },
];

const STEPS = [
  ["01", "Subscribe", "Choose individual or institutional plan and complete a secure payment."],
  ["02", "Receive Test ID", "We email a unique, single-use Test ID locked to one email."],
  ["03", "Verify Email", "Enter Test ID and verify your email via OTP."],
  ["04", "Complete Assessment", "Answer Likert items across the domains you chose."],
  ["05", "Receive Report", "A detailed PDF report is auto-emailed to you and your institution."],
];

export default function Home() {
  const [domains, setDomains] = useState([]);
  useEffect(() => {
    api.get("/public/domains").then((r) => setDomains(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #0A0A0B 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        </div>
        <div className="container-msis pt-16 pb-20 lg:pt-24 lg:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="overline mb-5" data-testid="hero-overline">Management Specialization Inclination Scale</div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-ink" data-testid="hero-title">
                Discover the management specialization <span className="text-navy">that fits you best.</span>
              </h1>
              <p className="mt-6 text-lg text-slate2 max-w-2xl leading-relaxed">
                MSIS is a scientifically grounded psychometric assessment that helps MBA, PGDM, and BBA students identify inclination across <b className="text-ink">9 specialization domains</b> — and choose with evidence rather than peer pressure.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/take-test" className="btn-primary" data-testid="hero-take-test-btn">Take the Test
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </Link>
                <Link to="/institutions" className="btn-ghost" data-testid="hero-institutions-btn">For Institutions</Link>
                <Link to="/sample-report" className="btn-ghost" data-testid="hero-sample-btn">View Sample Report</Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                <Stat n="9" label="Domains" />
                <Stat n="45" label="Constructs" />
                <Stat n="225" label="Items" />
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative bg-white border border-border p-6 rounded-md">
                <div className="overline mb-3">Sample Profile</div>
                <div className="space-y-3">
                  {[
                    { code: "ADB", name: "Analytics and Digital Business", score: 4.42, band: "Very High Inclination" },
                    { code: "SC", name: "Strategy and Consulting", score: 4.18, band: "High Inclination" },
                    { code: "MKT", name: "Marketing", score: 3.96, band: "High Inclination" },
                    { code: "IE", name: "Innovation and Entrepreneurship", score: 3.75, band: "High Inclination" },
                    { code: "FIN", name: "Finance", score: 3.41, band: "Moderate Inclination" },
                  ].map((r, i) => (
                    <div key={r.code} className="flex items-center gap-3">
                      <div className="font-mono text-xs text-slate2 w-5">{String(i + 1).padStart(2, "0")}</div>
                      <div className="font-mono text-[11px] px-1.5 py-0.5 border border-border text-slate2">{r.code}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.name}</div>
                        <div className="h-1 mt-1 bg-subtle rounded-full overflow-hidden">
                          <div className="h-full" style={{ width: `${(r.score / 5) * 100}%`, background: DOMAIN_COLORS[r.code] || "#002FA7" }} />
                        </div>
                      </div>
                      <div className="font-mono text-sm font-medium tabular-nums">{r.score.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                  <span className="overline">Bands</span>
                  <span className="text-xs text-slate2">VH · H · M · LM · L</span>
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3 w-full h-full bg-navy/5 -z-10 rounded-md"></div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE BULLETS */}
      <section className="border-y border-border bg-subtle">
        <div className="container-msis py-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {VALUE_BULLETS.map((v) => (
            <div key={v.k} className="bg-subtle p-6">
              <div className="overline">{v.k}</div>
              <div className="mt-3 font-display font-bold text-xl">{v.t}</div>
              <div className="text-sm text-slate2 mt-2">{v.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DOMAINS BENTO */}
      <section className="container-msis py-20" id="domains">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3">The 9 Domains</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">A complete map of management orientations.</h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base text-slate2 leading-relaxed">MSIS measures inclination across nine specialization domains. Each domain comprises five constructs which together reveal not just what specialization fits — but <i>why</i> it fits, at a construct level.</p>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-4" data-testid="domains-bento">
          {domains.map((d, i) => {
            const span = [0, 3].includes(i) ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-6 lg:col-span-4";
            return (
              <Link to="/domains" key={d.code} className={`card-grid block ${span}`} data-testid={`bento-${d.code}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-xs text-slate2">DOMAIN {String(d.no).padStart(2, "0")}</div>
                    <div className="font-display font-bold text-lg mt-1">{d.name}</div>
                  </div>
                  <span className="font-mono text-xs px-2 py-1 border border-border text-slate2" style={{ borderColor: DOMAIN_COLORS[d.code] + "55", color: DOMAIN_COLORS[d.code] }}>{d.code}</span>
                </div>
                <p className="text-sm text-slate2 mt-3 line-clamp-2">{d.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(d.constructs || []).slice(0, 3).map((c) => (
                    <span key={c.code} className="text-[10px] text-slate2 font-mono uppercase">{c.code}</span>
                  ))}
                  {d.constructs && d.constructs.length > 3 && <span className="text-[10px] text-slate2">+{d.constructs.length - 3} more</span>}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-ink text-white py-20">
        <div className="container-msis">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="overline text-white/60 mb-3">How It Works</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Five steps from sign-up to insight.</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-base text-white/70 leading-relaxed">A streamlined, secure flow that protects student data and ensures one-test-per-Test-ID integrity.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10">
            {STEPS.map(([k, t, d]) => (
              <div key={k} className="bg-ink p-6">
                <div className="font-mono text-xs text-white/50">{k}</div>
                <div className="font-display font-bold text-lg mt-2">{t}</div>
                <div className="text-sm text-white/60 mt-2">{d}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/take-test" className="inline-flex items-center gap-2 bg-white text-ink hover:bg-gold hover:text-ink px-6 py-3 rounded-md font-medium transition-colors" data-testid="home-cta-take-test">Take the Test</Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 border border-white/20 text-white hover:bg-white hover:text-ink px-6 py-3 rounded-md font-medium transition-colors" data-testid="home-cta-pricing">View Pricing</Link>
          </div>
        </div>
      </section>

      {/* INSTITUTIONS BAND */}
      <section className="container-msis py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-3">For Institutions</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Run MSIS for an entire batch.</h2>
            <p className="mt-4 text-base text-slate2 leading-relaxed">Generate Test IDs in bulk, distribute via institution code, and access aggregate dashboards with domain-wise batch analytics, completion tracking, and downloadable per-student reports.</p>
            <ul className="mt-5 space-y-2 text-sm text-slate2">
              {["Institution Code + Test IDs in bulk", "Batch & program tagging", "Aggregate analytics by domain", "Per-student downloadable reports", "Slab-based institutional pricing"].map((x) => (
                <li key={x} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-navy"></span> {x}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex gap-3">
              <Link to="/institutions" className="btn-primary" data-testid="home-institutions-btn">Request Institutional Demo</Link>
              <Link to="/pricing" className="btn-ghost">See Slab Pricing</Link>
            </div>
          </div>
          <div className="relative">
            <img src="https://images.pexels.com/photos/1251861/pexels-photo-1251861.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                 alt="Students on campus" className="w-full h-[440px] object-cover rounded-md" />
            <div className="absolute -bottom-4 -left-4 bg-white border border-border p-4 rounded-md max-w-[260px]">
              <div className="overline">Aggregate snapshot</div>
              <div className="mt-2 font-display font-bold text-lg">Batch of 240 students</div>
              <div className="text-sm text-slate2">Top inclination: Analytics & Digital Business (avg 4.12)</div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display font-bold text-3xl tracking-tight">{n}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
